/**
 * FilterSidebar - Sidebar fixa de filtros para o Dashboard
 *
 * Similar à sidebar de menu, com dois modos:
 * - Minimizado: ícone de filtro + seta para expandir
 * - Expandido: filtros completos organizados em seções
 *
 * ATUALIZADO: Agora usa FilterContext para estado global dos filtros.
 * Pode ser usado em qualquer página do sistema.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useFilters } from '@/contexts/FilterContext';

// Constantes exportadas para uso em outros componentes
export const FILTER_SIDEBAR_WIDTH = {
  collapsed: 48,  // px quando minimizado
  expanded: 300,  // px quando expandido
} as const;
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SlidersHorizontal,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  X,
  Store,
  Tag,
  CreditCard,
  FileText,
  UserCheck,
  Hash,
  CheckCircle,
  RotateCcw,
  Plus,
  Lock,
} from 'lucide-react';
import { useCardStyle } from '@/features/theme/contexts/ThemeContext';
import type { DashboardFilters, FilterOptions } from '@/hooks/useDashboardMetrics';

// Tipos de filtros disponíveis
type FilterType = 'loja' | 'produto' | 'forma_pagamento' | 'tipo_item' | 'afiliado' | 'sku' | 'status' | 'has_affiliate';

interface FilterConfig {
  key: FilterType;
  title: string;
  icon: React.ReactNode;
  optionsKey: keyof FilterOptions;
  filterKey: keyof DashboardFilters;
}

// Configuração de TODOS os filtros disponíveis
const ALL_FILTER_CONFIGS: FilterConfig[] = [
  { key: 'loja', title: 'Loja', icon: <Store className="h-4 w-4" />, optionsKey: 'lojas', filterKey: 'loja' },
  { key: 'produto', title: 'Produto', icon: <Tag className="h-4 w-4" />, optionsKey: 'produtos', filterKey: 'produto_nome' },
  { key: 'forma_pagamento', title: 'Forma de Pagamento', icon: <CreditCard className="h-4 w-4" />, optionsKey: 'formasPagamento', filterKey: 'forma_pagamento' },
  { key: 'tipo_item', title: 'Tipo de Item', icon: <FileText className="h-4 w-4" />, optionsKey: 'tiposItem', filterKey: 'tipo_item_venda_texto' },
  { key: 'afiliado', title: 'Afiliado', icon: <UserCheck className="h-4 w-4" />, optionsKey: 'afiliados', filterKey: 'afiliado_nome' },
  { key: 'sku', title: 'SKU', icon: <Hash className="h-4 w-4" />, optionsKey: 'skus', filterKey: 'sku' },
  { key: 'status', title: 'Status', icon: <CheckCircle className="h-4 w-4" />, optionsKey: 'statusList', filterKey: 'status' },
];

// Presets de filtros por página
export type FilterPreset = 'all' | 'sales' | 'dre' | 'minimal';

const FILTER_PRESETS: Record<FilterPreset, FilterType[]> = {
  // Todos os filtros (padrão para dashboard/DRE)
  all: ['loja', 'produto', 'forma_pagamento', 'tipo_item', 'afiliado', 'sku', 'status'],
  // Filtros para página de Vendas/Financeiro (sem SKU, forma_pagamento, tipo_item)
  sales: ['loja', 'produto', 'status', 'afiliado'],
  // Filtros para DRE
  dre: ['loja', 'produto', 'afiliado', 'status'],
  // Filtros mínimos
  minimal: ['loja', 'status'],
};

// Função para obter configs de filtros baseado no preset
function getFilterConfigs(preset: FilterPreset = 'all'): FilterConfig[] {
  const allowedTypes = FILTER_PRESETS[preset];
  return ALL_FILTER_CONFIGS.filter(config => allowedTypes.includes(config.key));
}

interface FilterSectionProps {
  title: string;
  icon: React.ReactNode;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  onRemove: () => void;
}

function FilterSection({ title, icon, options, selected, onChange, onRemove }: FilterSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter(v => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  if (options.length === 0) return null;

  return (
    <div className="border border-white/10 rounded-lg bg-white/5 overflow-hidden">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center gap-2 p-2.5 hover:bg-white/5 transition-colors">
            <span className="text-muted-foreground">{icon}</span>
            <span className="flex-1 text-left text-sm font-medium">{title}</span>
            {selected.length > 0 && (
              <Badge variant="secondary" className="h-5 px-1.5 text-xs bg-primary/20 text-primary">
                {selected.length}
              </Badge>
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-2.5 pb-2.5 space-y-2 border-t border-white/5 pt-2">
            <div className="flex items-center justify-between">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll();
                }}
                className="text-xs text-primary hover:underline"
              >
                {selected.length === options.length ? 'Limpar todos' : 'Selecionar todos'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                Remover
              </button>
            </div>
            <ScrollArea className="h-[120px]">
              <div className="space-y-1 pr-3">
                {options.map(option => (
                  <label
                    key={option}
                    className="flex items-center gap-2 py-1 px-2 rounded hover:bg-white/5 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selected.includes(option)}
                      onCheckedChange={() => handleToggle(option)}
                      className="h-3.5 w-3.5"
                    />
                    <span className="truncate flex-1" title={option}>
                      {option}
                    </span>
                  </label>
                ))}
              </div>
            </ScrollArea>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface HasAffiliateFilterProps {
  value: boolean | null | undefined;
  onChange: (value: boolean | null) => void;
  onRemove: () => void;
}

function HasAffiliateFilter({ value, onChange, onRemove }: HasAffiliateFilterProps) {
  return (
    <div className="border border-white/10 rounded-lg bg-white/5 overflow-hidden p-2.5">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">Com Afiliado</Label>
        </div>
        <button
          onClick={onRemove}
          className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
        >
          <X className="h-3 w-3" />
          Remover
        </button>
      </div>
      <div className="flex gap-1">
        <Button
          variant={value === null || value === undefined ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-3 text-xs flex-1"
          onClick={() => onChange(null)}
        >
          Todos
        </Button>
        <Button
          variant={value === true ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-3 text-xs flex-1"
          onClick={() => onChange(true)}
        >
          Sim
        </Button>
        <Button
          variant={value === false ? 'secondary' : 'ghost'}
          size="sm"
          className="h-7 px-3 text-xs flex-1"
          onClick={() => onChange(false)}
        >
          Não
        </Button>
      </div>
    </div>
  );
}

// Props são opcionais - se não fornecidas, usa o contexto global
interface FiltersSidebarProps {
  // Se fornecido, sobrescreve o contexto (para backwards compatibility)
  filters?: DashboardFilters;
  filterOptions?: FilterOptions;
  onFiltersChange?: (filters: DashboardFilters) => void;
  activeFiltersCount?: number;
  // Preset de filtros para mostrar (padrão: 'all')
  preset?: FilterPreset;
  // Mostrar filtro "Com Afiliado" (padrão: true)
  showHasAffiliate?: boolean;
}

export function FilterSidebar(props: FiltersSidebarProps) {
  const { cardStyleObject, borderClassName } = useCardStyle();
  const [activeFilterTypes, setActiveFilterTypes] = useState<Set<FilterType>>(new Set());

  // Usar contexto global de filtros
  const filterContext = useFilters();

  // Estado de expansão via contexto (para sincronizar com outros componentes)
  const isExpanded = filterContext.isFilterSidebarExpanded;
  const setIsExpanded = filterContext.setIsFilterSidebarExpanded;

  // Permitir sobrescrita via props (backwards compatibility)
  const filters = props.filters ?? filterContext.filters;
  const filterOptions = props.filterOptions ?? filterContext.filterOptions;
  const onFiltersChange = props.onFiltersChange ?? filterContext.setFilters;
  const activeFiltersCount = props.activeFiltersCount ?? filterContext.activeFiltersCount;

  // Preset de filtros - usar do contexto ou da prop (prop tem prioridade)
  const contextPreset = filterContext.currentPreset;
  const preset = props.preset ?? contextPreset ?? 'all';
  const showHasAffiliate = props.showHasAffiliate ?? true;

  // Obter configuração de filtros baseada no preset
  const FILTER_CONFIGS = useMemo(() => getFilterConfigs(preset), [preset]);

  // SEGURANÇA: Obter restrição de loja do usuário
  // Se o usuário tem restrição, ocultar o filtro de loja
  const { hasStoreRestriction, isStoreFilterLocked, lockedStoreFilterMessage: _lockedStoreFilterMessage } = filterContext;

  // Sincroniza activeFilterTypes com os filtros pré-definidos
  useEffect(() => {
    const typesFromFilters = new Set<FilterType>();

    FILTER_CONFIGS.forEach(config => {
      const filterValue = filters[config.filterKey];
      if (Array.isArray(filterValue) && filterValue.length > 0) {
        typesFromFilters.add(config.key);
      }
    });

    if (filters.has_affiliate !== null && filters.has_affiliate !== undefined) {
      typesFromFilters.add('has_affiliate');
    }

    // Só atualiza se houver diferença (evita loop infinito)
    if (typesFromFilters.size > 0) {
      setActiveFilterTypes(prev => {
        // Merge: mantém os existentes e adiciona os novos
        const merged = new Set([...prev, ...typesFromFilters]);
        // Verifica se realmente mudou
        if (merged.size !== prev.size || [...typesFromFilters].some(t => !prev.has(t))) {
          return merged;
        }
        return prev;
      });
    }
  }, [filters, FILTER_CONFIGS]);

  // Determina quais filtros têm opções disponíveis
  // SEGURANÇA: Oculta filtro de loja para usuários com restrição
  const availableFilters = useMemo(() => {
    return FILTER_CONFIGS.filter(config => {
      // Se usuário tem restrição de loja, não mostrar filtro de loja
      if (hasStoreRestriction && config.key === 'loja') {
        return false;
      }
      const options = filterOptions[config.optionsKey];
      return Array.isArray(options) && options.length > 0;
    });
  }, [filterOptions, hasStoreRestriction, FILTER_CONFIGS]);

  // Filtros que ainda podem ser adicionados (têm opções e não estão ativos)
  const addableFilters = useMemo(() => {
    return availableFilters.filter(config => !activeFilterTypes.has(config.key));
  }, [availableFilters, activeFilterTypes]);

  // Verifica se "Com Afiliado" pode ser adicionado
  const canAddHasAffiliate = showHasAffiliate && !activeFilterTypes.has('has_affiliate');

  const handleClearAll = () => {
    onFiltersChange({});
    setActiveFilterTypes(new Set());
  };

  const updateFilter = useCallback(<K extends keyof DashboardFilters>(
    key: K,
    value: DashboardFilters[K]
  ) => {
    const newFilters = { ...filters };
    if (Array.isArray(value) && value.length === 0) {
      delete newFilters[key];
    } else if (value === null || value === undefined) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    onFiltersChange(newFilters);
  }, [filters, onFiltersChange]);

  const addFilterType = (type: FilterType) => {
    setActiveFilterTypes(prev => new Set([...prev, type]));
  };

  const removeFilterType = (type: FilterType, filterKey: keyof DashboardFilters) => {
    setActiveFilterTypes(prev => {
      const newSet = new Set(prev);
      newSet.delete(type);
      return newSet;
    });
    // Limpa o filtro associado
    updateFilter(filterKey, type === 'has_affiliate' ? null : []);
  };

  const hasFilters = availableFilters.length > 0;

  // Helper para obter valores selecionados de um filtro
  const getFilterValues = (type: FilterType): string[] => {
    if (type === 'has_affiliate') {
      const val = filters.has_affiliate;
      if (val === true) return ['Sim'];
      if (val === false) return ['Não'];
      return [];
    }
    const config = FILTER_CONFIGS.find(c => c.key === type);
    if (!config) return [];
    const values = filters[config.filterKey];
    return Array.isArray(values) ? values : [];
  };

  // Helper para formatar lista de valores para tooltip
  const formatValuesForTooltip = (values: string[], maxItems = 3): string => {
    if (values.length === 0) return 'Nenhum selecionado';
    if (values.length <= maxItems) return values.join(', ');
    return `${values.slice(0, maxItems).join(', ')} +${values.length - maxItems}`;
  };

  // Gerar resumo de todos os filtros ativos
  const getActiveFiltersSummary = (): { title: string; values: string }[] => {
    const summary: { title: string; values: string }[] = [];

    [...activeFilterTypes].forEach(type => {
      const values = getFilterValues(type);
      if (values.length === 0) return;

      const config = FILTER_CONFIGS.find(c => c.key === type);
      const title = type === 'has_affiliate' ? 'Com Afiliado' : config?.title || type;
      summary.push({ title, values: formatValuesForTooltip(values) });
    });

    return summary;
  };

  return (
    <TooltipProvider delayDuration={0}>
      <motion.div
        initial={false}
        animate={{ width: isExpanded ? 300 : 48 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        data-theme-surface="true"
        className={cn(
          'fixed right-0 top-16 bottom-0 z-20',
          'border-l',
          borderClassName,
          'flex flex-col'
        )}
        style={cardStyleObject}
      >
        {/* Header - Modo Minimizado */}
        {!isExpanded && (
          <div className="flex flex-col items-center p-2 gap-2">
            {/* Seta para Expandir */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsExpanded(true)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Expandir filtros</TooltipContent>
            </Tooltip>

            {/* Ícone de Filtros */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 relative"
                  onClick={() => setIsExpanded(true)}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-[250px]">
                <p className="font-medium">Filtros</p>
                {activeFiltersCount > 0 ? (
                  <div className="mt-1.5 space-y-1">
                    {getActiveFiltersSummary().map(({ title, values }) => (
                      <div key={title} className="text-xs">
                        <span className="text-muted-foreground">{title}:</span>{' '}
                        <span className="text-foreground">{values}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">Nenhum filtro ativo</p>
                )}
              </TooltipContent>
            </Tooltip>

            {/* Indicadores de filtros ativos quando minimizado */}
            {activeFilterTypes.size > 0 && (
              <div className="flex flex-col gap-1 mt-2">
                {[...activeFilterTypes].map(type => {
                  const config = FILTER_CONFIGS.find(c => c.key === type);
                  if (!config && type !== 'has_affiliate') return null;

                  const icon = type === 'has_affiliate'
                    ? <UserCheck className="h-4 w-4" />
                    : config?.icon;

                  const filterKey = type === 'has_affiliate' ? 'has_affiliate' : config?.filterKey;
                  const selected = filterKey ? filters[filterKey as keyof DashboardFilters] : null;
                  const hasValue = type === 'has_affiliate'
                    ? selected !== null && selected !== undefined
                    : Array.isArray(selected) && selected.length > 0;

                  return (
                    <Tooltip key={type}>
                      <TooltipTrigger asChild>
                        <div
                          className={cn(
                            'w-8 h-8 rounded-md flex items-center justify-center',
                            hasValue
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground'
                          )}
                        >
                          <div className="relative">
                            {icon}
                            {hasValue && (
                              <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-[200px]">
                        <p className="font-medium">{type === 'has_affiliate' ? 'Com Afiliado' : config?.title}</p>
                        {hasValue && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatValuesForTooltip(getFilterValues(type), 5)}
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Header - Modo Expandido */}
        {isExpanded && (
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm">Filtros</span>
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="h-5 px-2 text-xs bg-primary text-primary-foreground">
                  {activeFiltersCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {activeFiltersCount > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handleClearAll}
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">Limpar todos</TooltipContent>
                </Tooltip>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setIsExpanded(false)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Content - Modo Expandido */}
        {isExpanded && (
          <ScrollArea className="flex-1">
            <div className="p-3 space-y-4">
              {/* Aviso de filtro de lojas bloqueado */}
              {isStoreFilterLocked && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-2">
                  <Lock className="h-4 w-4 text-amber-500 shrink-0" />
                  <span className="text-xs text-amber-400">
                    Filtro de lojas bloqueado nesta página
                  </span>
                </div>
              )}

              {!hasFilters ? (
                <div className="text-center py-8 text-muted-foreground">
                  <SlidersHorizontal className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum filtro disponível</p>
                  <p className="text-xs mt-1">Selecione um período com dados.</p>
                </div>
              ) : (
                <>
                  {/* Seção: Filtros Ativos */}
                  {activeFilterTypes.size > 0 && (
                    <div className="space-y-2">
                      {[...activeFilterTypes].map(type => {
                        if (type === 'has_affiliate') {
                          // Não mostrar se showHasAffiliate for false
                          if (!showHasAffiliate) return null;
                          return (
                            <HasAffiliateFilter
                              key={type}
                              value={filters.has_affiliate}
                              onChange={(v) => updateFilter('has_affiliate', v)}
                              onRemove={() => removeFilterType('has_affiliate', 'has_affiliate')}
                            />
                          );
                        }

                        const config = FILTER_CONFIGS.find(c => c.key === type);
                        if (!config) return null;

                        const options = filterOptions[config.optionsKey] as string[];
                        const selected = (filters[config.filterKey] as string[]) || [];

                        return (
                          <FilterSection
                            key={type}
                            title={config.title}
                            icon={config.icon}
                            options={options}
                            selected={selected}
                            onChange={(v) => updateFilter(config.filterKey, v)}
                            onRemove={() => removeFilterType(type, config.filterKey)}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Seção: Adicionar Filtros */}
                  {(addableFilters.length > 0 || canAddHasAffiliate) && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider px-1">
                        <Plus className="h-3 w-3" />
                        <span>Adicionar Filtros</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {addableFilters.map(config => (
                          <Button
                            key={config.key}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1.5 bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                            onClick={() => addFilterType(config.key)}
                          >
                            <Plus className="h-3 w-3" />
                            {config.title}
                          </Button>
                        ))}
                        {canAddHasAffiliate && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs gap-1.5 bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                            onClick={() => addFilterType('has_affiliate')}
                          >
                            <Plus className="h-3 w-3" />
                            Com Afiliado
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        )}

        {/* Footer - só mostra se tiver filtros ativos */}
        <AnimatePresence>
          {activeFiltersCount > 0 && isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-white/10 p-3"
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 bg-white/5 border-white/10 hover:bg-white/10"
                onClick={handleClearAll}
              >
                <X className="h-3.5 w-3.5" />
                Limpar {activeFiltersCount} filtro(s)
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </TooltipProvider>
  );
}
